import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { ToastrService } from 'ngx-toastr';
import { ref, Storage, getDownloadURL } from '@angular/fire/storage';
import { Recording } from '@sc/types';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { Session } from '@sc/types';
import { UserService } from '../../services/user/user.service';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

@Component({
  selector: 'sc-download-all-menu',
  templateUrl: './download-all-menu.component.html',
  styleUrls: ['./download-all-menu.component.scss'],
})
export class DownloadAllMenuComponent implements OnInit {
  @Input() session: Session;
  @Input() recordings: Array<Recording>;
  @Input() all: boolean;
  @Input() event: any;
  @Input() selectMultiple: any;
  activeUser$ = this.userService.activeUser$;
  enableZip = true;
  zipObject: {
    mp4: {
      zipProgress: number;
      isZippingFiles: boolean;
    };
    mp3: {
      zipProgress: number;
      isZippingFiles: boolean;
    };
    webm: {
      zipProgress: number;
      isZippingFiles: boolean;
    };
    wav: {
      zipProgress: number;
      isZippingFiles: boolean;
    };
  };

  mp4Downloads: Array<Recording> = [];
  webmDownloads: Array<Recording> = [];
  wavDownloads: Array<Recording> = [];
  mp3Downloads: Array<Recording> = [];

  constructor(
    private elementRef: ElementRef,
    private popoverController: PopoverController,
    private toastrService: ToastrService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private storage: Storage
  ) {}

  ngOnInit() {
    this.setupDownloads();
  }

  async downloadAll(format: string) {
    const downloads = this.elementRef.nativeElement.querySelectorAll(`.${format}Download`);
    if (this.enableZip) {
      if (this.recordings.length > 15) {
        this.toastrService.error(
          `Due to size complexitiy of individual recordings. We limit the amount of recordings that can be zipped to 15. Please try manually downlading each recording by disabling Zip toggle or lower the number of recordings selected.`,
          `Only up to 15 ${format} recordings can be zipped at a time. Please try manual download or select less.`,
          {
            progressBar: true,
            progressAnimation: 'decreasing',
            closeButton: true,
            tapToDismiss: false,
            timeOut: 5 * 5000,
            toastComponent: GeneralToastComponent,
          }
        );
        return;
      }
      this.zipObject[format].zipProgress = 25;
      this.zipObject[format].isZippingFiles = true;
      this.toastrService.info(
        `Downloading multiple files at once will create a single zip download for that format type. If this takes too long, please try manually downloading the files you need.`,
        `Preparing all ${format} recordings for download. Please wait.`,
        {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 5 * 5000,
          toastComponent: GeneralToastComponent,
        }
      );
      await this.downloadFilesAsZip(format);
      this.toastrService.success(null, `Successfully zipped all ${format} Recordings`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
      await this.analyticsService.track(`zipped downloaded all recordings`, { format });
    } else {
      downloads.forEach((download: any, index: number) => {
        setTimeout(() => {
          download.click();
        }, index * 1000);
      });
      await this.popoverController.dismiss();
      this.toastrService.success(null, `Successfully downloaded all ${format} Recordings`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
      await this.analyticsService.track(`downloaded all recordings`, { format });
    }
  }

  async downloadFilesAsZip(format: string) {
    const jszip = new JSZip();
    const selectedRecordings = this.recordings.map(async (recording) => {
      const url = recording[format];
      if (!Object.prototype.hasOwnProperty.call(recording, format)) return;
      if (!url || url === '' || url.length < 1) return;
      const filename = recording.fileName;
      const path = `${recording.showID}/${recording.sessionID}/${filename}.${format}`;
      const fileRef = ref(this.storage, path);
      const fileBlob = await getDownloadURL(fileRef)
        .then(async (fileUrl) => {
          const response = await fetch(fileUrl);
          return await response.blob();
        })
        .catch((err) => {
          console.log(err);
          return;
        });
      if (!fileBlob) return;
      jszip.file(`${filename}.${format}`, fileBlob);
      // Update Progress based on the number of recordings to find the percentage between 25 and 75
      const incrementScale = Math.floor((1 / this.recordings.length) * 50);
      this.zipObject[format].zipProgress += incrementScale;
      return { url, filename, path, fileBlob };
    });

    return await Promise.allSettled(selectedRecordings)
      .then(async (res: any) => {
        this.zipObject[format].zipProgress = 75;
        return jszip.generateAsync({ type: 'blob' }, (metadata) => {
          this.zipObject[format].zipProgress = Math.floor(metadata.percent * 0.25) + 75;
        });
      })
      .then((blob) => {
        this.zipObject[format].zipProgress = 100;
        this.zipObject[format].isZippingFiles = false;
        return saveAs(blob, `${format}_downloads.zip`);
      })
      .catch((error: any) => {
        this.toastrService.error(
          `There was an issue trying to zip up all your downloads. Please try manually downlading each recording by disabling Zip toggle.`,
          `Unable to zip up ${format} recordings for download. Please try manual download.`,
          {
            progressBar: true,
            progressAnimation: 'decreasing',
            closeButton: true,
            tapToDismiss: false,
            timeOut: 5 * 5000,
            toastComponent: GeneralToastComponent,
          }
        );
        console.warn(`⚠ ⚠  error ➡ ${JSON.stringify(error, null, 2)} ⚠ ⚠ `);
      });
  }

  async openMoreMenu() {
    await this.popoverController.dismiss();
  }

  toggleZip(event: ICustomEvent) {
    this.enableZip = event.detail.checked;
  }

  setupDownloads() {
    this.zipObject = {
      mp4: {
        zipProgress: this.zipObject?.mp4?.zipProgress || 0,
        isZippingFiles: this.zipObject?.mp4?.isZippingFiles || false,
      },
      mp3: {
        zipProgress: this.zipObject?.mp3?.zipProgress || 0,
        isZippingFiles: this.zipObject?.mp3?.isZippingFiles || false,
      },
      webm: {
        zipProgress: this.zipObject?.webm?.zipProgress || 0,
        isZippingFiles: this.zipObject?.webm?.isZippingFiles || false,
      },
      wav: {
        zipProgress: this.zipObject?.wav?.zipProgress || 0,
        isZippingFiles: this.zipObject?.wav?.isZippingFiles || false,
      },
    };
    this.mp4Downloads = this.recordings.filter((recording) => recording.mp4);
    this.webmDownloads = this.recordings.filter((recording) => recording.webm);
    this.wavDownloads = this.recordings.filter((recording) => recording.wav);
    this.mp3Downloads = this.recordings.filter((recording) => recording.mp3);
  }

  sendToDropBox(format: string) {
    this.popoverController.dismiss();

    this.toastrService.success(null, `Successfully uploaded all ${format} Recordings to Dropbox`, {
      progressBar: true,
      progressAnimation: 'decreasing',
      closeButton: true,
      tapToDismiss: false,
      timeOut: 5 * 1000,
      toastComponent: GeneralToastComponent,
    });

    this.analyticsService.track(`sent to dropbox`, { format });
  }
}
